import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Nav from "@/components/Nav";
import {
  ChartBar, Users, Buildings, FileText, Scales, CurrencyDollar,
  Files, Lightning, ArrowLeft, ChartPie, ChartLineUp,
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  LineChart, Line, CartesianGrid,
} from "recharts";
import { harmLabel, targetLabel } from "@/lib/constants";

const RED = "#FF3366";
const TEAL = "#00FFCC";
const SURFACE = "#1A1A1A";
const BG = "#0D0D0D";
const PIE_COLORS = ["#FF3366", "#00FFCC", "#FF8C42", "#B388FF", "#4CC9F0", "#F72585", "#FFD60A", "#7B2CBF", "#3A86FF", "#FB5607"];

export default function AdminAnalytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/analytics");
        setData(data);
      } catch (e) {
        if (e.response?.status === 403) setForbidden(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (!user) return <Navigate to="/login" replace />;

  if (forbidden) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] text-white">
        <Nav />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="admin-forbidden">
          <div className="border border-[#FF3366]/40 bg-[#FF3366]/5 p-12">
            <div className="font-mono-sf text-[10px] uppercase tracking-[0.3em] text-[#FF3366] mb-3">// 403</div>
            <h1 className="font-heading font-black uppercase tracking-tighter text-4xl mb-3">Admin Only.</h1>
            <p className="text-neutral-400 font-mono-sf text-sm mb-8">
              This page is restricted to platform operators. Your account is not in the admin allowlist.
            </p>
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 h-10 px-4 border border-[#1A1A1A] hover:bg-[#1A1A1A] font-mono-sf text-xs uppercase tracking-[0.25em] transition-colors"
            >
              <ArrowLeft size={14} weight="bold" /> Back to your dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] text-white">
        <Nav />
        <div className="max-w-[1400px] mx-auto px-6 py-20 font-mono-sf text-xs uppercase tracking-[0.3em] text-neutral-500" data-testid="admin-loading">
          Loading platform analytics...
        </div>
      </div>
    );
  }

  const { totals, top_corporations, harm_types, cases_by_day, agencies_leaderboard } = data;

  const statCards = [
    { i: Users, l: "Total Users", v: totals.users, sub: `+${totals.users_last_7d} last 7d` },
    { i: Scales, l: "Total Cases", v: totals.cases, sub: `+${totals.cases_last_7d} last 7d` },
    { i: FileText, l: "Drafts Generated", v: totals.drafts_generated, sub: `${(totals.drafts_generated / Math.max(totals.cases, 1)).toFixed(1)} per case` },
    { i: Files, l: "Evidence Files", v: totals.evidence_files, sub: "in vault" },
    { i: Lightning, l: "Actions Logged", v: totals.actions_logged, sub: "filed by users" },
    { i: CurrencyDollar, l: "Damages Claimed", v: `$${(totals.damages_claimed / 1000).toFixed(1)}k`, sub: "cumulative" },
  ];

  const harmData = harm_types.map((h) => ({ name: harmLabel(h.harm_type), value: h.count }));
  const corpData = top_corporations.map((c) => ({ name: c.corporation.length > 18 ? c.corporation.slice(0, 16) + "…" : c.corporation, count: c.count }));
  const agenciesData = agencies_leaderboard.map((a) => ({ name: targetLabel(a.target), count: a.count }));

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white" data-testid="admin-analytics-page">
      <Nav />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="font-mono-sf text-xs uppercase tracking-[0.3em] text-[#00FFCC] mb-3">// admin console</div>
            <h1 className="font-heading font-black uppercase tracking-tighter text-5xl md:text-6xl leading-none">
              Platform Analytics.
            </h1>
            <p className="mt-3 font-mono-sf text-xs uppercase tracking-[0.25em] text-neutral-500">
              Generated {new Date(data.generated_at).toLocaleString()}
            </p>
          </div>
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 h-10 px-4 border border-[#1A1A1A] hover:bg-[#1A1A1A] font-mono-sf text-xs uppercase tracking-[0.25em] transition-colors"
            data-testid="admin-back-to-dashboard"
          >
            <ArrowLeft size={14} weight="bold" /> User Dashboard
          </Link>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border border-[#1A1A1A] divide-x divide-y md:divide-y-0 divide-[#1A1A1A] mb-10" data-testid="admin-stat-cards">
          {statCards.map((s, i) => {
            const Icon = s.i;
            return (
              <div key={s.l} className="p-5" data-testid={`admin-stat-${i}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500">{s.l}</span>
                  <Icon size={16} weight="bold" className="text-[#00FFCC]" />
                </div>
                <div className="font-mono-sf text-3xl font-bold text-white">{s.v}</div>
                <div className="font-mono-sf text-[10px] uppercase tracking-wider text-neutral-500 mt-2">{s.sub}</div>
              </div>
            );
          })}
        </div>

        {/* Chart grid */}
        <div className="grid lg:grid-cols-2 gap-px bg-[#1A1A1A] border border-[#1A1A1A] mb-10">
          {/* Cases per day */}
          <ChartPanel
            title="Cases Filed (30d)"
            tag="// activity"
            icon={ChartLineUp}
            testid="chart-cases-per-day"
          >
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={cases_by_day} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                <CartesianGrid stroke={SURFACE} strokeDasharray="3 3" />
                <XAxis dataKey="day" stroke="#666" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} tickFormatter={(d) => d.slice(5)} interval={4} />
                <YAxis stroke="#666" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: BG, border: `1px solid ${SURFACE}`, fontFamily: "JetBrains Mono", fontSize: 11 }} cursor={{ stroke: RED, strokeWidth: 1 }} />
                <Line type="monotone" dataKey="count" stroke={RED} strokeWidth={2} dot={false} activeDot={{ r: 4, fill: RED }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartPanel>

          {/* Harm type pie */}
          <ChartPanel
            title="Harm Type Breakdown"
            tag="// distribution"
            icon={ChartPie}
            testid="chart-harm-pie"
          >
            {harmData.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={harmData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                    {harmData.map((h, i) => <Cell key={h.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: BG, border: `1px solid ${SURFACE}`, fontFamily: "JetBrains Mono", fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
            {harmData.length > 0 && (
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-4 max-h-32 overflow-y-auto pr-2">
                {harmData.map((h, i) => (
                  <div key={h.name} className="flex items-center gap-2 font-mono-sf text-[10px] text-neutral-400">
                    <span className="w-2 h-2 flex-shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                    <span className="truncate">{h.name}</span>
                    <span className="ml-auto text-neutral-500">{h.value}</span>
                  </div>
                ))}
              </div>
            )}
          </ChartPanel>

          {/* Top corporations */}
          <ChartPanel
            title="Top Targeted Corporations"
            tag="// adversaries"
            icon={Buildings}
            testid="chart-top-corps"
          >
            {corpData.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={corpData} layout="vertical" margin={{ top: 8, right: 12, left: 20, bottom: 0 }}>
                  <XAxis type="number" stroke="#666" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" stroke="#666" width={110} tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
                  <Tooltip contentStyle={{ background: BG, border: `1px solid ${SURFACE}`, fontFamily: "JetBrains Mono", fontSize: 11 }} cursor={{ fill: `${RED}20` }} />
                  <Bar dataKey="count" fill={RED} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartPanel>

          {/* Agencies leaderboard */}
          <ChartPanel
            title="Agencies Contacted"
            tag="// filings"
            icon={ChartBar}
            testid="chart-agencies"
          >
            {agenciesData.length === 0 ? (
              <EmptyChart hint="No filings logged yet." />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={agenciesData} margin={{ top: 8, right: 12, left: -12, bottom: 40 }}>
                  <XAxis dataKey="name" stroke="#666" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} angle={-25} textAnchor="end" interval={0} />
                  <YAxis stroke="#666" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: BG, border: `1px solid ${SURFACE}`, fontFamily: "JetBrains Mono", fontSize: 11 }} cursor={{ fill: `${TEAL}20` }} />
                  <Bar dataKey="count" fill={TEAL} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartPanel>
        </div>

        {/* User management */}
        <UserManagementPanel />
      </div>
    </div>
  );
}

function UserManagementPanel() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users${q ? `?search=${encodeURIComponent(q)}` : ""}`);
      setUsers(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const togglePaid = async (u) => {
    setSavingId(u.id);
    try {
      await api.patch(`/admin/users/${u.id}/paid`, { is_paid: !u.is_paid });
      setUsers((list) => list.map((x) => x.id === u.id ? { ...x, is_paid: !u.is_paid } : x));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="border border-[#1A1A1A] bg-[#0D0D0D] p-6" data-testid="user-management-panel">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <div className="font-mono-sf text-[10px] uppercase tracking-[0.3em] text-[#00FFCC]">// activation</div>
          <h2 className="font-heading font-bold uppercase tracking-tight text-2xl mt-1">User Access</h2>
        </div>
        <div className="flex gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(search)}
            placeholder="Search by email prefix..."
            className="h-10 bg-[#0D0D0D] border border-[#1A1A1A] focus:border-[#00FFCC] focus:outline-none px-3 text-white placeholder:text-neutral-600 font-mono-sf text-xs min-w-[240px]"
            data-testid="user-search-input"
          />
          <button
            onClick={() => load(search)}
            className="h-10 px-4 border border-[#1A1A1A] hover:bg-[#1A1A1A] font-mono-sf text-[10px] uppercase tracking-[0.25em] transition-colors"
            data-testid="user-search-btn"
          >
            Search
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 font-mono-sf text-xs uppercase tracking-[0.25em] text-neutral-500">Loading users...</div>
      ) : (
        <div className="overflow-x-auto" data-testid="user-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#1A1A1A] text-left">
                <th className="py-3 pr-4 font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500">Email</th>
                <th className="py-3 pr-4 font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500">Name</th>
                <th className="py-3 pr-4 font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500">Joined</th>
                <th className="py-3 pr-4 font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500">Status</th>
                <th className="py-3 font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500 text-right">Access</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-[#1A1A1A] hover:bg-[#0D0D0D]" data-testid={`user-row-${u.id}`}>
                  <td className="py-3 pr-4 font-mono-sf text-xs text-white break-all">{u.email}</td>
                  <td className="py-3 pr-4 font-mono-sf text-xs text-neutral-400">{u.full_name}</td>
                  <td className="py-3 pr-4 font-mono-sf text-[10px] text-neutral-500">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="py-3 pr-4">
                    {u.is_paid ? (
                      <span className="inline-flex items-center gap-2 font-mono-sf text-[10px] uppercase tracking-[0.25em] text-[#00FFCC] border border-[#00FFCC]/40 px-2 py-0.5">
                        <span className="w-1.5 h-1.5 bg-[#00FFCC]" /> Activated
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2 font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500 border border-[#1A1A1A] px-2 py-0.5">
                        <span className="w-1.5 h-1.5 bg-neutral-600" /> Locked
                      </span>
                    )}
                  </td>
                  <td className="py-3 text-right">
                    <button
                      onClick={() => togglePaid(u)}
                      disabled={savingId === u.id}
                      className={`h-8 px-3 font-mono-sf text-[10px] uppercase tracking-[0.25em] font-bold transition-colors ${
                        u.is_paid
                          ? "border border-[#1A1A1A] hover:border-[#FF3366] hover:text-[#FF3366]"
                          : "bg-[#00FFCC] text-[#0D0D0D] hover:bg-[#33ffd4]"
                      } disabled:opacity-50`}
                      data-testid={`toggle-paid-${u.id}`}
                    >
                      {savingId === u.id ? "..." : u.is_paid ? "Revoke" : "Activate"}
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center font-mono-sf text-xs uppercase tracking-[0.25em] text-neutral-600">No users found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ChartPanel({ title, tag, icon: Icon, children, testid }) {
  return (
    <div className="bg-[#0D0D0D] p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-mono-sf text-[10px] uppercase tracking-[0.3em] text-[#00FFCC]">{tag}</div>
          <h3 className="font-heading font-bold uppercase tracking-tight text-lg mt-1">{title}</h3>
        </div>
        <Icon size={18} weight="bold" className="text-[#00FFCC]" />
      </div>
      {children}
    </div>
  );
}

function EmptyChart({ hint = "No data yet." }) {
  return (
    <div className="h-[240px] flex items-center justify-center border border-dashed border-[#1A1A1A]">
      <span className="font-mono-sf text-xs uppercase tracking-[0.25em] text-neutral-600">{hint}</span>
    </div>
  );
}
